/**
 * What an admin exception will do, said before it is done (LG-1004).
 *
 * These mirror `admin-participation-service.ts` closely enough that the
 * preview and the outcome agree; the service is still the authority, and
 * the page shows its refusal verbatim when they disagree. The two funding
 * questions are not mirrored but imported: the rules module is pure, so the
 * preview asks the same function the service does.
 */
import type {
  AdminRosterRow,
  AdminSprint,
} from '../../../lib/sprint/admin-sprint-query'
import type { SprintPhase } from '../../../lib/sprint/participation-transition'
import {
  sprintStatusCanCharge,
  weekIsFundable,
} from '../../../lib/sprint/sprint-funding-rules'

export interface Consequence {
  /** False when the service will refuse this row as it stands. */
  doable: boolean
  text: string
  /** The ledger line, when a credit moves or deliberately does not. */
  ledger?: string
}

/**
 * The slice of a roster row a preview reads. The last two are optional so a
 * candidate row -- which is never unfunded and never reduced -- fits too.
 * `reducedRequirements` is only asked whether it is set: the page holds the
 * tRPC shape, where `at` has become a string.
 */
type PersonRow = Pick<AdminRosterRow, 'clientName' | 'status' | 'balance'> &
  Partial<Pick<AdminRosterRow, 'willBeUnfunded'>> & {
    reducedRequirements?: Pick<
      NonNullable<AdminRosterRow['reducedRequirements']>,
      'reason'
    > | null
  }

type SprintForConsequence = Pick<AdminSprint, 'isFree' | 'status'>

export function describeAdd(
  row: PersonRow,
  sprint: SprintForConsequence,
  phase: SprintPhase,
): Consequence {
  const name = row.clientName
  if (phase === 'after_end') {
    return {
      doable: false,
      text: 'The Sprint has ended. Nothing can change on it now.',
    }
  }
  if (!sprintStatusCanCharge(sprint.status)) {
    return {
      doable: false,
      text: 'The Sprint is suspended, so no credit can be spent on it.',
    }
  }
  if (row.willBeUnfunded) {
    return {
      doable: false,
      text: `${name} is already in the Sprint but has no credit for it. Grant a credit instead and they lock in with everyone else.`,
    }
  }
  if (phase === 'before_lock_in') {
    const verb = row.status === 'absent' ? 'joins' : 'rejoins'
    return {
      doable: true,
      text: `${name} ${verb} the Sprint. Nothing is charged until lock-in.`,
    }
  }
  if (!weekIsFundable(sprint, row.balance)) {
    return {
      doable: false,
      text: `${name} has no credits. Grant a credit first — the grant and the lock-in are two ledger rows, deliberately.`,
    }
  }
  if (sprint.isFree) {
    return {
      doable: true,
      text: `${name} joins the locked Sprint at no cost.`,
      ledger: 'consume, delta 0',
    }
  }
  return {
    doable: true,
    text: `${name} joins the locked Sprint and is charged now.`,
    ledger: `consume, delta −1 · balance ${row.balance} → ${row.balance - 1}`,
  }
}

/**
 * A grant is the remedy where a removal used to be: one ledger row, no
 * status change. The page refuses it only once the Sprint has ended, because
 * the page locks everything then; the ledger itself would take it.
 */
export function describeGrant(row: PersonRow, phase: SprintPhase): Consequence {
  const name = row.clientName
  if (phase === 'after_end') {
    return {
      doable: false,
      text: 'The Sprint has ended. Nothing can change on it now.',
    }
  }
  return {
    doable: true,
    text: `${name} gets one credit. Nothing else moves: it funds their next unfunded week, or a late add.`,
    ledger: `admin_adjust, delta +1 · balance ${row.balance} → ${row.balance + 1}`,
  }
}

/**
 * Reduced requirements is a note on a running week, not a move: the row
 * stays `locked`, no credit moves, and the plan is left alone. The service
 * takes it only on a running Sprint, only for a locked client, and the page
 * says so before the admin commits; a row already flagged is skipped rather
 * than re-stamped, because clearing is its own button on the row.
 */
export function describeReduce(
  row: PersonRow,
  phase: SprintPhase,
): Consequence {
  const name = row.clientName
  if (phase === 'after_end') {
    return {
      doable: false,
      text: 'The Sprint has ended. Nothing can change on it now.',
    }
  }
  if (phase !== 'after_start') {
    return {
      doable: false,
      text: 'Reduced requirements apply to a running Sprint only.',
    }
  }
  if (row.status !== 'locked') {
    return {
      doable: false,
      text: `${name} is not locked into this week, so there is nothing to reduce.`,
    }
  }
  if (row.reducedRequirements) {
    return {
      doable: false,
      text: `${name} already has reduced requirements this week. Clear it from the row to start over.`,
    }
  }
  return {
    doable: true,
    text: `${name} keeps the week with less expected of them. No credit moves and the plan stays as it is.`,
  }
}
